import {
  DEFAULT_FUNCTION_PANE_HEIGHT,
  DEFAULT_FUNCTION_PANE_WIDTH,
  DEFAULT_PANE_HEIGHT,
  DEFAULT_PANE_WIDTH,
} from "../Constants";
import {
  Action,
  ActionType,
  Canvas,
  CanvasLayoutPane,
  EvaluateExpressionType,
  Pane,
  PaneFlag,
  PaneInput,
  PaneRenderOutput,
  PaneType,
  Rect,
} from "../Types";
import {
  applyTextChangeSet,
  areBabelPluginsEqual,
  assert,
  createEjectedPanes,
  getEjectedTemplatePaneId,
  getNewPane,
  getNewPaneRect,
  setFlag,
} from "../Utils";
import { EvalPaneTemplates } from "./EvalPaneTemplates";

function replacePaneIdFromPaneInputs(
  pane: Pane,
  replacePaneIds: string[],
  newPaneId: string | undefined
): Pane {
  if ("inputs" in pane && pane.inputs !== undefined) {
    let hasChange = false;
    const newPaneInputs = pane.inputs.map((paneInput): PaneInput => {
      const inputPaneSource = paneInput.source;
      if (
        inputPaneSource !== undefined &&
        replacePaneIds.includes(inputPaneSource[0])
      ) {
        hasChange = true;
        return {
          ...paneInput,
          source:
            newPaneId !== undefined
              ? [newPaneId, inputPaneSource[1]]
              : undefined,
        };
      }
      return paneInput;
    });
    let renderOutput: PaneRenderOutput | undefined;
    if (
      pane.type === PaneType.Evaluate &&
      pane.renderOutput?.[0] === "custom" &&
      pane.renderOutput[1] !== undefined &&
      replacePaneIds.includes(pane.renderOutput[1][0])
    ) {
      hasChange = true;
      if (newPaneId !== undefined) {
        renderOutput = ["custom", [newPaneId, pane.renderOutput[1][1]]];
      } else {
        renderOutput = ["custom"];
      }
    }
    if (
      pane.type === PaneType.Evaluate &&
      pane.renderOutput?.[0] === "react" &&
      pane.renderOutput[1]?.ReactDOM !== undefined &&
      replacePaneIds.includes(pane.renderOutput[1].ReactDOM[0])
    ) {
      hasChange = true;
      if (newPaneId !== undefined) {
        renderOutput = [
          "react",
          { ReactDOM: [newPaneId, pane.renderOutput[1].ReactDOM[1]] },
        ];
      } else {
        renderOutput = ["react"];
      }
    }
    if (hasChange) {
      const rv: any = {
        ...pane,
        inputs: newPaneInputs,
      };
      if (renderOutput !== undefined) {
        rv.renderOutput = renderOutput;
      }
      return rv;
    }
  }
  if (pane.type === PaneType.LLM) {
    if (
      pane.inputSource?.[0] !== undefined &&
      replacePaneIds.includes(pane.inputSource[0])
    ) {
      return {
        ...pane,
        inputSource:
          newPaneId !== undefined
            ? [newPaneId, pane.inputSource[1]]
            : undefined,
      };
    }
  }
  if (pane.type === PaneType.EvaluateTemplate) {
    let hasChange = false;
    const newTemplateInputSources: Record<string, any> = {};
    for (const [paneInputId, [inputPaneId, outputIndex]] of Object.entries(
      pane.templateInputSources
    )) {
      if (replacePaneIds.includes(inputPaneId)) {
        hasChange = true;
        if (newPaneId !== undefined) {
          newTemplateInputSources[paneInputId] = [newPaneId, outputIndex];
        }
      } else {
        newTemplateInputSources[paneInputId] = [inputPaneId, outputIndex];
      }
    }
    if (hasChange) {
      return {
        ...pane,
        templateInputSources: newTemplateInputSources,
      };
    }
  }
  return pane;
}

function paneUpdater(
  canvas: Canvas,
  paneId: string,
  updater: (pane: Pane) => Pane
): Canvas {
  return {
    ...canvas,
    panes: canvas.panes.map((p) => (p.id === paneId ? updater(p) : p)),
  };
}

function layoutPaneUpdater(
  canvas: Canvas,
  layoutId: string,
  paneId: string,
  updater: (layoutPane: CanvasLayoutPane) => CanvasLayoutPane
) {
  return {
    ...canvas,
    layouts: canvas.layouts.map((layout) => {
      if (layout.id !== layoutId) {
        return layout;
      }
      let hasChange = false;
      const newLayoutPanes = layout.panes.map((p) => {
        if (p.id === paneId) {
          const newLayoutPane = updater(p);
          if (p !== newLayoutPane) {
            hasChange = true;
          }
          return newLayoutPane;
        }
        return p;
      });
      if (hasChange) {
        return {
          ...layout,
          panes: newLayoutPanes,
        };
      }
      return layout;
    }),
  };
}

function checkPaneInputSource(
  canvas: Canvas,
  paneInputSource: [paneId: string, outputIndex: number] | undefined
): [paneId: string, outputIndex: number] | undefined {
  if (
    paneInputSource !== undefined &&
    canvas.panes.every((p) => p.id !== paneInputSource[0])
  ) {
    return undefined;
  }
  return paneInputSource;
}

export function canvasReducer(canvas: Canvas, action: Action): Canvas {
  switch (action[0]) {
    case ActionType.NoOp:
    case ActionType.GenerateLLMText:
    case ActionType.PresenceCursor:
    case ActionType.PresencePaneId:
    case ActionType.PresenceTextRange:
    case ActionType.PresenceName: {
      return canvas;
    }

    case ActionType.SetCanvasName: {
      const { name } = action[1];
      return {
        ...canvas,
        name,
      };
    }

    case ActionType.CreatePane: {
      const {
        paneId,
        type,
        position,
        size,
        name,
        expression,
        editorHeight,
        templateId,
        metadata,
      } = action[1];
      const rect: Rect = getNewPaneRect(type, position, size);
      const panesZ = canvas.panes.map((p) => p.z);
      const maxZIndex = panesZ.length > 0 ? Math.max(...panesZ) : 0;
      const z = maxZIndex + 1;

      let newPane: Pane;
      switch (type) {
        case PaneType.Evaluate:
        case PaneType.EvaluateGlobal:
        case PaneType.Import:
        case PaneType.InputText:
        case PaneType.State:
        case PaneType.EnvironmentVariable:
        case PaneType.Library:
        case PaneType.LLM: {
          newPane = getNewPane({ id: paneId, rect, z, name }, type);
          if (newPane.type === PaneType.Evaluate && expression !== undefined) {
            newPane.expression = expression;
          }
          break;
        }
        case PaneType.EvaluateTemplate: {
          newPane = {
            id: paneId,
            rect,
            z,
            type: PaneType.EvaluateTemplate,
            name,
            templateId: templateId!,
            metadata: metadata ?? {},
            templateInputSources: {},
            editorHeight: undefined,
          };
          break;
        }
        default: {
          throw new Error("not implemented");
        }
      }
      if ("editorHeight" in newPane && editorHeight !== undefined) {
        newPane.editorHeight = editorHeight;
      }
      return { ...canvas, panes: [...canvas.panes, newPane] };
    }

    case ActionType.CreateTransformPane: {
      const { paneId, position, paneInput, expression, name } = action[1];
      const rect: Rect = [
        position[0],
        position[1],
        DEFAULT_PANE_WIDTH,
        DEFAULT_PANE_HEIGHT,
      ];
      const panesZ = canvas.panes.map((p) => p.z);
      const maxZIndex = panesZ.length > 0 ? Math.max(...panesZ) : 0;
      const newPane: Pane = {
        id: paneId,
        type: PaneType.Evaluate,
        rect,
        z: maxZIndex + 1,
        inputs: [
          {
            ...paneInput,
            source:
              paneInput.source !== undefined &&
              !canvas.panes.some((p) => p.id === paneInput.source![0])
                ? undefined
                : paneInput.source,
          },
        ],
        expression,
        babelPlugins: [],
        autorun: true,
        editorHeight: undefined,
      };
      if (name !== undefined) {
        newPane.name = name;
      }
      return {
        ...canvas,
        panes: [...canvas.panes, newPane],
      };
    }

    case ActionType.CreateFunctionPane: {
      const { paneId, position, paneIds } = action[1];
      const rect: Rect = [
        position[0],
        position[1],
        DEFAULT_FUNCTION_PANE_WIDTH,
        DEFAULT_FUNCTION_PANE_HEIGHT,
      ];
      const panesZ = canvas.panes.map((p) => p.z);
      const maxZIndex = panesZ.length > 0 ? Math.max(...panesZ) : 0;
      const newPane: Pane = {
        id: paneId,
        type: PaneType.Function,
        rect,
        z: maxZIndex + 1,
        paneIds,
      };
      return {
        ...canvas,
        panes: [...canvas.panes, newPane],
      };
    }

    case ActionType.EjectTemplatePane: {
      const { paneId } = action[1];
      const templatePane = canvas.panes.find((p) => p.id === paneId);
      if (
        templatePane === undefined ||
        templatePane.type !== PaneType.EvaluateTemplate
      ) {
        return canvas;
      }
      const template = EvalPaneTemplates[templatePane.templateId];
      if (template === undefined) {
        return canvas;
      }
      const constructedTemplate = template.construct(templatePane.metadata);
      const ejectedPanes: Pane[] = createEjectedPanes(
        templatePane,
        templatePane.templateInputSources,
        constructedTemplate.panes
      );
      const mainEjectedPaneId = getEjectedTemplatePaneId(paneId, 0);
      const newPanes = [
        ...canvas.panes
          .filter((p) => p.id !== paneId)
          .map((p) =>
            replacePaneIdFromPaneInputs(p, [paneId], mainEjectedPaneId)
          ),
        ...ejectedPanes,
      ];
      const newLayouts = canvas.layouts.map((layout) => {
        return {
          ...layout,
          panes: layout.panes.map((p) =>
            p.id === paneId
              ? {
                  ...p,
                  id: mainEjectedPaneId,
                }
              : p
          ),
        };
      });
      return {
        ...canvas,
        panes: newPanes,
        layouts: newLayouts,
      };
    }

    case ActionType.PastePanes: {
      const { panes } = action[1];
      const existingPaneIds = canvas.panes.map((p) => p.id);
      const allPaneIds = [...existingPaneIds, ...panes.map((p) => p.id)];
      const clipboardZIndexOffset =
        canvas.panes.length > 0 ? Math.max(...canvas.panes.map((p) => p.z)) : 0;
      const newPanes = panes.map((p) => {
        const newPane: Pane = {
          ...p,
          z: clipboardZIndexOffset + p.z,
        };
        if ("inputs" in newPane) {
          newPane.inputs = newPane.inputs.map((paneInput) =>
            paneInput.source !== undefined &&
            !allPaneIds.includes(paneInput.source[0])
              ? { ...paneInput, source: undefined }
              : paneInput
          );
        }
        if ("templateInputSources" in newPane) {
          newPane.templateInputSources = Object.fromEntries(
            Object.entries(newPane.templateInputSources).filter(([, source]) =>
              allPaneIds.includes(source[0])
            )
          );
        }
        return newPane;
      });
      return {
        ...canvas,
        panes: [...canvas.panes, ...newPanes],
      };
    }

    case ActionType.SetPaneRect: {
      const { paneId, rect } = action[1];
      return paneUpdater(canvas, paneId, (p) => ({ ...p, rect }));
    }

    case ActionType.SetPanePosition: {
      const { paneId, position: xy } = action[1];
      return paneUpdater(canvas, paneId, (p) => ({
        ...p,
        rect: [xy[0], xy[1], p.rect[2], p.rect[3]],
      }));
    }

    case ActionType.SetPaneZ: {
      const { paneId, z } = action[1];
      return paneUpdater(canvas, paneId, (p) => ({ ...p, z }));
    }

    case ActionType.SetPaneCollapse: {
      const { paneId, collapse } = action[1];
      return paneUpdater(canvas, paneId, (p) => ({
        ...p,
        flags: setFlag(p.flags ?? 0, PaneFlag.Collapse, collapse),
      }));
    }

    case ActionType.PushPaneInput: {
      let { paneId, paneInput } = action[1];
      return paneUpdater(canvas, paneId, (p) => {
        assert(p.type === PaneType.Evaluate);
        return {
          ...p,
          inputs: [
            ...p.inputs,
            {
              ...paneInput,
              source: checkPaneInputSource(canvas, paneInput.source),
            },
          ],
        };
      });
    }

    case ActionType.SetPaneInputName: {
      const { paneId, paneInputId, name } = action[1];
      return paneUpdater(canvas, paneId, (p) => {
        assert(p.type === PaneType.Evaluate);
        return {
          ...p,
          inputs: p.inputs.map((paneInput) =>
            paneInput.id === paneInputId ? { ...paneInput, name } : paneInput
          ),
        };
      });
    }

    case ActionType.SetPaneInputSource: {
      const { paneId, paneInputId, source } = action[1];
      return paneUpdater(canvas, paneId, (p) => {
        assert(p.type === PaneType.Evaluate);
        return {
          ...p,
          inputs: p.inputs.map((paneInput) =>
            paneInput.id === paneInputId
              ? {
                  ...paneInput,
                  source: checkPaneInputSource(canvas, source),
                }
              : paneInput
          ),
        };
      });
    }

    case ActionType.SetPaneTemplateInputSource: {
      const { paneId, paneInputId, source } = action[1];
      return paneUpdater(canvas, paneId, (p) => {
        assert(p.type === PaneType.EvaluateTemplate);
        const templateInputSources = {
          ...p.templateInputSources,
        };
        if (
          source !== undefined &&
          canvas.panes.some((p) => p.id === source[0])
        ) {
          templateInputSources[paneInputId] = source;
        } else {
          delete templateInputSources[paneInputId];
        }
        return {
          ...p,
          templateInputSources,
        };
      });
    }

    case ActionType.SetPaneLLMInputSource: {
      const { paneId, source } = action[1];
      return paneUpdater(canvas, paneId, (p) => {
        assert(p.type === PaneType.LLM);
        return {
          ...p,
          inputSource: checkPaneInputSource(canvas, source),
        };
      });
    }

    case ActionType.SetFunctionPaneInputPaneId: {
      const { paneId, inputPaneId } = action[1];
      return paneUpdater(canvas, paneId, (p) => {
        assert(p.type === PaneType.Function);
        return { ...p, inputPaneId };
      });
    }

    case ActionType.SetFunctionPaneOutputPaneId: {
      const { paneId, outputPaneId } = action[1];
      return paneUpdater(canvas, paneId, (p) => {
        assert(p.type === PaneType.Function);
        return { ...p, outputPaneId };
      });
    }

    case ActionType.DeletePaneInput: {
      const { paneId, paneInputId } = action[1];
      return paneUpdater(canvas, paneId, (p) => {
        assert(p.type === PaneType.Evaluate);
        return {
          ...p,
          inputs: p.inputs.filter((paneInput) => paneInput.id !== paneInputId),
        };
      });
    }

    case ActionType.SetPaneEditorHeight: {
      const { paneId, editorHeight } = action[1];
      return paneUpdater(canvas, paneId, (p) => ({ ...p, editorHeight }));
    }

    case ActionType.SetPaneName: {
      const { paneId, name } = action[1];
      return paneUpdater(canvas, paneId, (p) => {
        if (p.id === paneId) {
          return { ...p, name };
        } else {
          return p;
        }
      });
    }

    case ActionType.DeletePanes: {
      const { paneIds } = action[1];
      return {
        ...canvas,
        panes: canvas.panes
          .filter((p) => !paneIds.includes(p.id))
          .map((p) => replacePaneIdFromPaneInputs(p, paneIds, undefined)),
        layouts: canvas.layouts.map((layout) => ({
          ...layout,
          panes: layout.panes.filter((p) => !paneIds.includes(p.id)),
        })),
      };
    }

    case ActionType.UndoDeletePanes: {
      const { deletions } = action[1];
      let updatedPanes = [...canvas.panes];
      const allPaneIds = [
        ...canvas.panes.map((p) => p.id),
        ...deletions.map(({ pane }) => pane.id),
      ];
      for (let { pane } of deletions) {
        if ("inputs" in pane) {
          pane = {
            ...pane,
            inputs: pane.inputs.map((input) => {
              if (
                input.source !== undefined &&
                !allPaneIds.includes(input.source[0])
              ) {
                return { ...input, source: undefined };
              }
              return input;
            }),
          };
        }
        if ("inputSource" in pane) {
          pane = {
            ...pane,
            inputSource:
              pane.inputSource !== undefined &&
              !allPaneIds.includes(pane.inputSource[0])
                ? undefined
                : pane.inputSource,
          };
        }
        if ("templateInputSources" in pane) {
          pane = {
            ...pane,
            templateInputSources: Object.fromEntries(
              Object.entries(pane.templateInputSources).filter(([, source]) =>
                allPaneIds.includes(source[0])
              )
            ),
          };
        }
        updatedPanes.push(pane);
      }
      const paneInputUpdates = deletions
        .map(({ pane, outputs }) =>
          outputs.map((output) => ({
            outputPaneId: output.paneId,
            source: [pane.id, output.outputIndex] as [string, number],
            output,
          }))
        )
        .flat();
      updatedPanes = updatedPanes.map((pane) => {
        let hasChange = false;
        const updatedPane = { ...pane };
        if ("inputs" in updatedPane) {
          const newInputs = updatedPane.inputs.map((paneInput): PaneInput => {
            if (paneInput.source !== undefined) {
              return paneInput;
            }
            const update = paneInputUpdates.find(
              (update) =>
                update.outputPaneId === pane.id &&
                "paneInputId" in update.output &&
                update.output.paneInputId === paneInput.id
            );
            if (update !== undefined) {
              hasChange = true;
              return {
                ...paneInput,
                source: update.source,
              };
            }
            return paneInput;
          });
          updatedPane.inputs = newInputs;
        }
        if (
          "renderOutput" in updatedPane &&
          updatedPane.renderOutput?.[0] === "custom" &&
          updatedPane.renderOutput[1] === undefined
        ) {
          const update = paneInputUpdates.find(
            (update) =>
              update.outputPaneId === updatedPane.id &&
              "renderOutput" in update.output &&
              update.output.renderOutput[0] === "custom"
          );
          if (update !== undefined) {
            hasChange = true;
            updatedPane.renderOutput = [
              updatedPane.renderOutput[0],
              update.source,
            ];
          }
        }
        if (
          "renderOutput" in updatedPane &&
          updatedPane.renderOutput?.[0] === "react" &&
          updatedPane.renderOutput[1]?.ReactDOM === undefined
        ) {
          const update = paneInputUpdates.find(
            (update) =>
              update.outputPaneId === updatedPane.id &&
              "renderOutput" in update.output &&
              update.output.renderOutput[0] === "react-dom"
          );
          if (update !== undefined) {
            hasChange = true;
            updatedPane.renderOutput = [
              updatedPane.renderOutput[0],
              { ReactDOM: update.source },
            ];
          }
        }
        if ("inputSource" in updatedPane) {
          const update = paneInputUpdates.find(
            (update) =>
              update.outputPaneId === pane.id &&
              "llmInput" in update.output &&
              update.output.llmInput === true
          );
          if (update !== undefined) {
            hasChange = true;
            updatedPane.inputSource = update.source;
          }
        }
        if ("templateInputSources" in updatedPane) {
          const templatePaneUpdates = paneInputUpdates.filter(
            (update) => update.outputPaneId === updatedPane.id
          );
          if (templatePaneUpdates.length > 0) {
            const updatedTemplateInputSources = {
              ...updatedPane.templateInputSources,
            };
            for (const update of templatePaneUpdates) {
              if (
                "templateInputSource" in update.output &&
                !(
                  update.output.templateInputSource in
                  updatedTemplateInputSources
                )
              ) {
                hasChange = true;
                updatedTemplateInputSources[update.output.templateInputSource] =
                  update.source;
              }
            }
            updatedPane.templateInputSources = updatedTemplateInputSources;
          }
        }
        if (hasChange) {
          return updatedPane;
        }
        return pane;
      });
      const updatedLayouts = canvas.layouts.map((layout) => {
        let updatedLayoutPanes = layout.panes;
        for (const { pane, layouts } of deletions) {
          if (layouts[layout.id] !== undefined) {
            if (updatedLayoutPanes.some((p) => p.id === pane.id)) {
              updatedLayoutPanes = updatedLayoutPanes.map((p) =>
                p.id === pane.id ? layouts[layout.id] : p
              );
            } else {
              updatedLayoutPanes = [...updatedLayoutPanes, layouts[layout.id]];
            }
          }
        }
        if (updatedLayoutPanes !== layout.panes) {
          return { ...layout, panes: updatedLayoutPanes };
        }
        return layout;
      });
      return {
        ...canvas,
        panes: updatedPanes,
        layouts: updatedLayouts,
      };
    }

    case ActionType.SetPaneExpression: {
      const { paneId, expression } = action[1];
      return paneUpdater(canvas, paneId, (p) => {
        assert(
          p.type === PaneType.Evaluate || p.type === PaneType.EvaluateGlobal
        );
        return { ...p, expression };
      });
    }

    case ActionType.UpdatePaneCodemirror: {
      const { paneId, f, changes: changesJSON } = action[1];
      return paneUpdater(canvas, paneId, (p) => {
        if (f === "expression") {
          assert(
            p.type === PaneType.Evaluate || p.type === PaneType.EvaluateGlobal
          );
        } else if (f === "text") {
          assert(p.type === PaneType.InputText);
        }
        return {
          ...p,
          // @ts-ignore
          [f]: applyTextChangeSet(p[f], changesJSON),
        };
      });
    }

    case ActionType.SetPaneInitialExpression: {
      const { paneId, initialExpression } = action[1];
      return paneUpdater(canvas, paneId, (p) => {
        assert(p.type === PaneType.State);
        return { ...p, initialExpression };
      });
    }

    case ActionType.SetPaneMetadata: {
      const { paneId, metadata } = action[1];
      return paneUpdater(canvas, paneId, (p) => {
        assert(p.type === PaneType.EvaluateTemplate);
        return { ...p, metadata };
      });
    }

    case ActionType.SetPaneRenderOutput: {
      const { paneId, renderOutput } = action[1];
      return paneUpdater(canvas, paneId, (p) => {
        assert(p.type === PaneType.Evaluate);
        return { ...p, renderOutput };
      });
    }

    case ActionType.AddPaneBabelPlugin: {
      const { paneId, babelPlugin } = action[1];
      assert(
        babelPlugin === "transform-react-jsx" || Array.isArray(babelPlugin)
      );
      return paneUpdater(canvas, paneId, (p) => {
        assert(p.type === PaneType.Evaluate);
        if (
          p.babelPlugins === undefined ||
          p.babelPlugins.find((p) => areBabelPluginsEqual(p, babelPlugin)) ===
            undefined
        ) {
          return {
            ...p,
            babelPlugins: [...(p.babelPlugins ?? []), babelPlugin],
          };
        }
        return p;
      });
    }

    case ActionType.RemovePaneBabelPlugin: {
      const { paneId, babelPlugin } = action[1];
      assert(
        babelPlugin === "transform-react-jsx" || Array.isArray(babelPlugin)
      );
      return paneUpdater(canvas, paneId, (p) => {
        assert(p.type === PaneType.Evaluate);
        const babelPluginIndex = p.babelPlugins?.findIndex((plugin) =>
          areBabelPluginsEqual(plugin, babelPlugin)
        );
        if (
          p.babelPlugins !== undefined &&
          babelPluginIndex !== undefined &&
          babelPluginIndex > -1
        ) {
          return {
            ...p,
            babelPlugins: p.babelPlugins.filter(
              (plugin) => !areBabelPluginsEqual(plugin, babelPlugin)
            ),
          };
        }
        return p;
      });
    }

    case ActionType.SetPaneExpressionType: {
      const { paneId, expressionType } = action[1];
      return paneUpdater(canvas, paneId, (p) => {
        assert(p.type === PaneType.Evaluate);
        const rv = { ...p };
        if (expressionType === EvaluateExpressionType.Expression) {
          delete rv.expressionType;
        } else {
          rv.expressionType = expressionType;
        }
        return rv;
      });
    }

    case ActionType.SetPaneAutorun: {
      const { paneId, autorun } = action[1];
      return paneUpdater(canvas, paneId, (p) => {
        assert(
          p.type === PaneType.Evaluate ||
            p.type === PaneType.EvaluateGlobal ||
            p.type === PaneType.EvaluateTemplate
        );
        return { ...p, autorun };
      });
    }

    case ActionType.SetPaneModule: {
      const { paneId, module } = action[1];
      return paneUpdater(canvas, paneId, (p) => {
        assert(p.type === PaneType.Import);
        return { ...p, module };
      });
    }

    case ActionType.SetPaneUseDefault: {
      const { paneId, useDefault } = action[1];
      return paneUpdater(canvas, paneId, (p) => {
        assert(p.type === PaneType.Import);
        return { ...p, useDefault };
      });
    }

    case ActionType.SetPaneIsGlobal: {
      const { paneId, isGlobal } = action[1];
      return paneUpdater(canvas, paneId, (p) => {
        assert(p.type === PaneType.Import);
        return { ...p, isGlobal };
      });
    }

    case ActionType.SetPaneText: {
      const { paneId, text } = action[1];
      return paneUpdater(canvas, paneId, (p) => {
        assert(p.type === PaneType.InputText);
        return { ...p, text };
      });
    }

    case ActionType.SetPaneEnvironmentKey: {
      const { paneId, key } = action[1];
      return paneUpdater(canvas, paneId, (p) => {
        assert(p.type === PaneType.EnvironmentVariable);
        return { ...p, key };
      });
    }

    case ActionType.SetPaneStateControl: {
      const { paneId, control } = action[1];
      return paneUpdater(canvas, paneId, (p) => {
        assert(p.type === PaneType.State);
        return { ...p, control };
      });
    }

    case ActionType.SetPaneLLMConfig: {
      const { paneId, key, value } = action[1];
      return paneUpdater(canvas, paneId, (p) => {
        assert(p.type === PaneType.LLM);
        return {
          ...p,
          config: {
            ...p.config,
            [key]: value,
          },
        };
      });
    }

    case ActionType.SetPaneRectsMulti: {
      const { paneRects } = action[1];
      return {
        ...canvas,
        panes: canvas.panes.map((p) => {
          if (paneRects[p.id] !== undefined) {
            return { ...p, rect: paneRects[p.id] };
          } else {
            return p;
          }
        }),
      };
    }

    case ActionType.CreateLayout: {
      const { layoutId, name } = action[1];
      return {
        ...canvas,
        layouts: [
          ...canvas.layouts,
          {
            id: layoutId,
            name,
            panes: [],
          },
        ],
      };
    }

    case ActionType.SetDefaultLayout: {
      const { layoutId } = action[1];
      if (
        layoutId !== undefined &&
        !canvas.layouts.some((layout) => layout.id === layoutId)
      ) {
        return canvas;
      }
      return {
        ...canvas,
        defaultLayoutId: layoutId,
      };
    }

    case ActionType.AddPaneToLayout: {
      const { layoutId, paneId, rect, z, editorHeight } = action[1];
      return {
        ...canvas,
        layouts: canvas.layouts.map((layout) => {
          if (
            layout.id !== layoutId ||
            layout.panes.some((p) => p.id === paneId)
          ) {
            return layout;
          }
          return {
            ...layout,
            panes: [...layout.panes, { id: paneId, rect, z, editorHeight }],
          };
        }),
      };
    }

    case ActionType.SetLayoutPaneRect: {
      const { layoutId, paneId, rect } = action[1];
      return layoutPaneUpdater(canvas, layoutId, paneId, (paneMetadata) => ({
        ...paneMetadata,
        rect,
      }));
    }

    case ActionType.SetLayoutPanePosition: {
      const { layoutId, paneId, position } = action[1];
      return layoutPaneUpdater(canvas, layoutId, paneId, (paneMetadata) => ({
        ...paneMetadata,
        rect: [...position, paneMetadata.rect[2], paneMetadata.rect[3]],
      }));
    }

    case ActionType.SetLayoutPaneZ: {
      const { layoutId, paneId, z } = action[1];
      return layoutPaneUpdater(canvas, layoutId, paneId, (paneMetadata) => ({
        ...paneMetadata,
        z,
      }));
    }

    case ActionType.SetLayoutPaneEditorHeight: {
      const { layoutId, paneId, editorHeight } = action[1];
      return layoutPaneUpdater(canvas, layoutId, paneId, (paneMetadata) => ({
        ...paneMetadata,
        editorHeight,
      }));
    }

    case ActionType.SetLayoutPaneRender: {
      const { layoutId, paneId, render } = action[1];
      return layoutPaneUpdater(canvas, layoutId, paneId, (paneMetadata) => ({
        ...paneMetadata,
        render,
      }));
    }

    case ActionType.RemovePaneFromLayout: {
      const { layoutId, paneId } = action[1];
      return {
        ...canvas,
        layouts: canvas.layouts.map((layout) => {
          if (layout.id !== layoutId) {
            return layout;
          }
          return {
            ...layout,
            panes: layout.panes.filter((p) => p.id !== paneId),
          };
        }),
      };
    }

    case ActionType.SetLayoutPaneRectsMulti: {
      const { layoutId, paneRects } = action[1];
      return {
        ...canvas,
        layouts: canvas.layouts.map((layout) => {
          if (layout.id !== layoutId) {
            return layout;
          }
          return {
            ...layout,
            panes: layout.panes.map((p) => {
              if (paneRects[p.id] === undefined) {
                return p;
              }
              return {
                ...p,
                rect: [...paneRects[p.id]],
              };
            }),
          };
        }),
      };
    }

    case ActionType.SetLayoutName: {
      const { layoutId, name } = action[1];
      return {
        ...canvas,
        layouts: canvas.layouts.map((layout) =>
          layout.id === layoutId ? { ...layout, name } : layout
        ),
      };
    }

    case ActionType.DeleteLayout: {
      const { layoutId } = action[1];
      return {
        ...canvas,
        layouts: canvas.layouts.filter((layout) => layout.id !== layoutId),
        defaultLayoutId:
          canvas.defaultLayoutId === layoutId
            ? undefined
            : canvas.defaultLayoutId,
      };
    }

    default: {
      throw new Error(`Unexpected action ${action[0]}`);
    }
  }
}
